import { postHrmApi, type HrmApiAction } from '@/lib/hrm-api';
import { readWorkflowResponse } from '@/lib/workflow-webhook';
import { ServiceDeskRuleError } from './service-desk.types';

/**
 * The service desk actions (routes of the hrm-service-desk function, src/lib/hrm-api.ts). Every write to the
 * five application schemas goes through one of them: they save with the service credential and check the
 * caller's permission, ownership, department and the application's status themselves, so nothing the browser
 * sends can approve its own application or change someone else's.
 */
export type ServiceDeskWorkflow = Extract<
  HrmApiAction,
  `${'NOC' | 'BYOD' | 'BUSINESS_CARD' | 'ID_CARD' | 'METLIFE'}_APPLICATION_${string}`
>;

interface RefusalData {
  /** The translation key of the refusal (service-desk / generic localization module). */
  MessageKey?: string;
}

/**
 * Runs a service desk action. A refusal throws a `ServiceDeskRuleError` with its translation key (or
 * the workflow's English message, for invalid input), which the global error toast shows; on success
 * the id of the saved application is returned.
 */
export const runServiceDeskWorkflow = async (
  workflow: ServiceDeskWorkflow,
  body: Record<string, unknown>
): Promise<string> => {
  const response = await postHrmApi(workflow, body);
  const result = readWorkflowResponse<RefusalData>(response, 'SERVICE_DESK_SAVE_FAILED');
  if (!result.IsSuccessful) {
    throw new ServiceDeskRuleError(result.Data?.MessageKey || result.Message);
  }
  return result.EntityId;
};
